// .pen: ech8X/Rtdvn/yL5V2/SK14u (Selfie · Revelação · primeira|subida|descida|estável)
// + eF6BI (ds/selfie/reveal-mark)
import {
  ArrowRight,
  Equals,
  TrendDown,
  TrendUp,
} from '@phosphor-icons/react';

import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { SelfieReportLinkCard } from '../SelfieReportLinkCard';
import { paths } from '../../../routes/paths';
import pt from '../../../locales/pt';
import { cn } from '../../../utils/cn';
import { formatEUR } from '../../../utils/money';
import {
  formatRevelationCopy,
  monthLongLabel,
} from '../../../lib/selfieDisplayUtils';

function format(template, params) {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    params[key] === undefined ? `{${key}}` : String(params[key]),
  );
}

const MARK_TONE = {
  up: {
    glow: 'bg-sage-glow-soft',
    circle: 'border-sage-border bg-sage-tint',
    icon: 'text-sage-on-tint',
    Icon: TrendUp,
  },
  down: {
    glow: 'bg-warning-glow',
    circle: 'border-border bg-warning-tint',
    icon: 'text-warning-fg',
    Icon: TrendDown,
  },
  flat: {
    glow: 'bg-info-glow',
    circle: 'border-border bg-info-tint',
    icon: 'text-info-fg',
    Icon: Equals,
  },
};

// .pen: eF6BI (ds/selfie/reveal-mark)
function RevelationMark({ variant }) {
  const tone = MARK_TONE[variant] || MARK_TONE.up;
  const Icon = tone.Icon;
  return (
    <span className="relative flex size-28 items-center justify-center" aria-hidden>
      <span className={cn('absolute inset-0 rounded-ds-full blur-[12px]', tone.glow)} />
      <span
        className={cn(
          'relative flex size-[88px] items-center justify-center rounded-ds-full border',
          tone.circle,
        )}
      >
        <Icon className={cn('size-10', tone.icon)} aria-hidden />
      </span>
    </span>
  );
}

const EYEBROW_TONE = {
  up: 'text-sage-on-tint',
  down: 'text-warning-fg',
  flat: 'text-info-fg',
};

/**
 * Clímax do ritual — revelação (from→to / marco zero + link para o relatório IA).
 * .pen: "Selfie · Revelação · primeira|subida|descida|estável"
 */
export function SelfieRevelationScreen({
  snapshot,
  timeline = [],
  onCloseCycle,
  closing = false,
  onboarding = false,
}) {
  const copy = pt.snapshots.ritual.revelation;
  const previousSnapshot =
    timeline.length > 1 ? timeline[timeline.length - 2] : null;
  const revelation = formatRevelationCopy({ snapshot, previousSnapshot, timeline });
  const monthLabel = monthLongLabel(snapshot?.reference_month);
  const monthLower = monthLabel.toLowerCase();
  const isFirst = revelation.variant === 'first';
  const markVariant = isFirst ? 'up' : revelation.variant;

  let body = null;
  if (isFirst) {
    body = copy.firstBody;
  } else if (revelation.variant === 'up') {
    body = format(copy.upBody, { amount: formatEUR(Math.abs(revelation.delta)) });
  } else if (revelation.variant === 'down') {
    body = format(copy.downBody, { amount: formatEUR(Math.abs(revelation.delta)) });
  } else {
    body = copy.flatBody;
  }

  const isDown = revelation.variant === 'down';
  const reportHref = `${paths.selfieReport(snapshot.id)}${onboarding ? '?onboarding=1' : ''}`;

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col items-center gap-space-8 px-1 pb-6 pt-2">
      <div className="flex flex-col items-center gap-space-4 text-center">
        <RevelationMark variant={markVariant} />

        <p className={cn('type-section', EYEBROW_TONE[markVariant])}>
          {format(copy.cheer, { month: monthLabel })}
        </p>

        {isFirst ? (
          <div className="flex flex-col items-center gap-space-3">
            <h1 className="font-display text-type-title-page font-semibold leading-[1.25] tracking-tight text-foreground">
              {copy.firstMarker}
            </h1>
            <p className="font-display text-type-display-hero font-bold leading-[1.05] tracking-tight text-foreground">
              {formatEUR(revelation.current)}
            </p>
            <Badge status="sage" className="border border-sage-border">
              {copy.firstBadge}
            </Badge>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-space-2">
            <div className="flex items-center gap-space-2">
              <span className="font-display text-type-body font-semibold text-muted-foreground">
                {formatEUR(revelation.previous)}
              </span>
              <ArrowRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            </div>
            <p className="font-display text-type-display-hero font-bold leading-[1.05] tracking-tight text-foreground">
              {formatEUR(revelation.current)}
            </p>
          </div>
        )}

        <p className="max-w-[290px] text-type-body leading-[1.55] text-muted-foreground">
          {body}
        </p>
        <p className="sr-only">
          {isFirst
            ? null
            : format(copy.fromTo, {
                from: formatEUR(revelation.previous),
                to: formatEUR(revelation.current),
              })}
        </p>
      </div>

      <SelfieReportLinkCard
        href={reportHref}
        title={isDown ? copy.reportTitleDown : copy.reportTitle}
        subtitle={isDown ? copy.reportSubtitleDown : copy.reportSubtitle}
      />

      <Button
        type="button"
        variant="primary"
        size="md"
        className="w-full justify-center"
        onClick={onCloseCycle}
        loading={closing}
      >
        {onboarding ? format(copy.closeCycleMonth, { month: monthLower }) : copy.closeCycle}
      </Button>
    </div>
  );
}

export default SelfieRevelationScreen;
